import React, { useState, useEffect } from 'react';
import Vapi from '@vapi-ai/web';
import { SpeakerHigh } from "@phosphor-icons/react";

// Create a global instance to share across components
let vapiInstance = null;
let isCallActive = false;

// Add a global subscriber list for call status changes
const callStatusSubscribers = new Set();

// Function to notify all subscribers of call status changes
const notifyCallStatusChange = (status) => {
  console.log('Notifying all subscribers of call status change:', status);
  isCallActive = status;
  console.log('Number of subscribers:', callStatusSubscribers.size);
  callStatusSubscribers.forEach(callback => {
    try {
      callback(status);
    } catch (error) {
      console.error('Error in call status subscriber callback:', error);
    }
  });
};

// Initialize the Vapi instance
const initVapi = () => {
  if (!vapiInstance) {
    vapiInstance = new Vapi('69b91a0d-ebe2-4ac7-99ee-430d014761c1');
    
    // Set up global event listeners
    vapiInstance.on('call-start', () => {
      console.log('Call has started');
      notifyCallStatusChange(true);
    });
    
    vapiInstance.on('call-end', () => {
      console.log('Call has ended');
      notifyCallStatusChange(false);
    });
    
    vapiInstance.on('error', (error) => {
      console.error('Vapi error:', error);
      notifyCallStatusChange(false); // Ensure we clean up status on error
    });
  }
  
  return vapiInstance;
};

// Start a call
export const startCall = async () => {
  const vapi = initVapi();
  if (!vapi) return;
  
  try {
    // Get user details from localStorage
    const userDetails = JSON.parse(localStorage.getItem('userDetails')) || {};
    const userName = userDetails.first_name || 'User';
    const userId = userDetails.id || null;
    
    console.log('Starting call with user ID:', userId);
    
    // Create the assistantOverrides object according to the documentation
    const assistantOverrides = {
      variableValues: {
        name: userName,
        user_id: userId
      }
    };
    
    console.log('Starting call with assistant ID:', '6f2fb948-19c4-4cc3-bf7a-252f71583c17');
    console.log('Using overrides:', assistantOverrides);
    
    // Start the call with the proper format for assistantOverrides
    await vapi.start("6f2fb948-19c4-4cc3-bf7a-252f71583c17", assistantOverrides);
    // Manually set call active in case the event hasn't fired yet
    setTimeout(() => {
      if (!isCallActive) {
        notifyCallStatusChange(true);
      }
    }, 1000);
  } catch (error) {
    console.error('Error starting call:', error);
    notifyCallStatusChange(false); // Reset status on error
  }
};

// Stop a call
export const stopCall = () => {
  if (vapiInstance) {
    try {
      vapiInstance.stop();
      // Manually set call inactive in case the event hasn't fired yet
      setTimeout(() => {
        if (isCallActive) {
          notifyCallStatusChange(false);
        }
      }, 1000);
    } catch (error) {
      console.error('Error stopping call:', error);
      notifyCallStatusChange(false); // Reset status on error
    }
  }
};

// Subscribe to call status changes
export const subscribeToCallStatus = (callback) => {
  console.log('New subscription to call status');
  if (typeof callback !== 'function') {
    console.error('Callback must be a function');
    return () => {};
  }
  
  callStatusSubscribers.add(callback);
  
  // Return current status immediately
  console.log('Returning initial call status:', isCallActive);
  try {
    callback(isCallActive);
  } catch (error) {
    console.error('Error in initial call status callback:', error);
  }
  
  // Return a function to unsubscribe
  return () => {
    console.log('Unsubscribing from call status');
    callStatusSubscribers.delete(callback);
  };
};

// Get current call status
export const getCallStatus = () => {
  return isCallActive;
};

function TalkToKale({ externalIsActive, externalHandleClick }) {
  const [localIsCallActive, setLocalIsCallActive] = useState(false);
  const [volumeLevel, setVolumeLevel] = useState(0);

  useEffect(() => {
    // Initialize Vapi instance
    const vapi = initVapi();
    
    // Set up event listeners for this component instance
    const handleCallStart = () => {
      setLocalIsCallActive(true);
    };
    
    const handleCallEnd = () => {
      setLocalIsCallActive(false);
    };
    
    const handleVolumeLevel = (volume) => {
      setVolumeLevel(volume);
    };
    
    vapi.on('call-start', handleCallStart);
    vapi.on('call-end', handleCallEnd);
    vapi.on('volume-level', handleVolumeLevel);
    
    // Set initial state based on global state
    setLocalIsCallActive(isCallActive);
    
    // Cleanup function
    return () => {
      vapi.off('call-start', handleCallStart);
      vapi.off('call-end', handleCallEnd);
      vapi.off('volume-level', handleVolumeLevel);
    };
  }, []);

  // Use external state if provided, otherwise use local state
  const isActive = externalIsActive !== undefined ? externalIsActive : localIsCallActive;

  const handleClick = () => {
    if (externalHandleClick) {
      // Use external click handler if provided
      externalHandleClick();
    } else {
      // Otherwise use local handler
      if (localIsCallActive) {
        stopCall();
      } else {
        startCall();
      }
    }
  };

  const tooltipText = isActive ? 'End call with Kale' : 'Talk to Kale';

  return (
    <button
      className={`talk-to-kale-icon ${isActive ? 'active' : ''}`}
      onClick={handleClick}
      title={tooltipText}
      aria-label={tooltipText}
    >
      <SpeakerHigh size={20} weight="fill" color="white" />
    </button>
  );
}

export default TalkToKale; 